import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { useSelector } from "react-redux"
import { Button } from "@/components/ui/button"
import { Code2, X } from "lucide-react"

interface DebugFieldButtonProps {
  fieldConfig: any
  fieldValue?: any
  relatedData?: any
  label?: string
}

export const DebugFieldButton = ({ fieldConfig, fieldValue, relatedData, label }: DebugFieldButtonProps) => {
  const [open, setOpen] = useState(false)
  const debugMode = useSelector((state: any) => state.ui?.debugMode) || false

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  if (!debugMode) {
    return null
  }

  const debugData = {
    fieldConfiguration: fieldConfig,
    currentValue: fieldValue,
    ...(relatedData && { relatedData })
  }

  const jsonString = JSON.stringify(debugData, null, 2)

  const dialogContent = open ? (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 999999 }}
      onClick={() => setOpen(false)}
    >
      <div
        className="bg-black/50 absolute inset-0"
        onClick={() => setOpen(false)}
      />
      <div
        className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[85vh] flex flex-col"
        style={{ zIndex: 1000000 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Debug: {label || 'Field Configuration'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Field configuration and current values
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          <pre className="text-xs bg-gray-50 p-4 rounded border overflow-x-auto">
            <code>{jsonString}</code>
          </pre>
        </div>
      </div>
    </div>
  ) : null

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
        title={`Debug: ${label || 'Field Configuration'}`}
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
      >
        <Code2 className="h-3 w-3" />
      </Button>
      {typeof document !== 'undefined' && open && createPortal(dialogContent, document.body)}
    </>
  )
}

export default DebugFieldButton

